import { useEffect, useRef } from "react";

import { useDocumentViewerStore } from "../documentViewerStore";
import { sendCoord, subscribeCoord } from "./channel";
import type { DocumentPayload, SessionState } from "./messages";
/**
 * Opener-side broadcaster for the dual-window document viewer (OR-22558).
 * Mounted in the window that hosts the Docs & Msgs panel (the Review page); it
 * tells the viewer window which document is active.
 *
 * Emits `doc-focused` (only while a document is focused and this tab holds the
 * opener lock):
 * - whenever the focused document or `sessionState` changes, and
 * - in reply to the viewer's `request-doc` handshake (sent when the viewer
 *   mounts / reloads).
 *
 * Deliberately no heartbeat, no `visibilityState` gating, and no
 * `pagehide`/`doc-blurred`: the viewer is load-and-persist, so a backgrounded,
 * reloaded, or closed opener must not blank a document the analyst is reading.
 * A visible-again or reloaded opener re-asserts state via the handshake.
 * @param {SessionState} sessionState - The opener's current session signal.
 */
export function useDocumentBroadcaster(sessionState: SessionState): void {
  const focusedDocument = useDocumentViewerStore((state) => state.focusedDocument);

  // Ref so the handshake subscription (mounted once) always reads the latest
  // payload without re-subscribing each render.
  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useRef requires an explicit initial value
  const payloadReference = useRef<DocumentPayload | undefined>(undefined);
  payloadReference.current = focusedDocument ? { ...focusedDocument, sessionState } : undefined;

  // Emit `doc-focused` whenever the focused document or session state changes.
  // Any tab can send updates; BroadcastChannel uses last-writer-wins semantics.
  useEffect(() => {
    if (payloadReference.current) {
      sendCoord({ type: "doc-focused", ...payloadReference.current });
    }
  }, [focusedDocument, sessionState]);

  // Reply to the viewer's mount-time handshake with the current document.
  // Any tab can respond; the viewer receives from all tabs (last-writer-wins).
  useEffect(() => {
    const unsubscribe = subscribeCoord((message) => {
      if (message.type === "request-doc" && payloadReference.current) {
        sendCoord({ type: "doc-focused", ...payloadReference.current });
      }
    });
    return unsubscribe;
  }, []);

  // Broadcast app focus on gain only (never on blur — the viewer popup taking
  // focus blurs this window on every normal click, and the viewer must stay
  // load-and-persist through that, per the module doc comment above). This lets
  // the subscriber detect a genuinely different deal's tab gaining focus (its
  // appId differs from the one the viewer last saw).
  useEffect(() => {
    const handleFocus = () => {
      if (payloadReference.current?.appId) {
        const message = { type: "app-context-changed" as const, appId: payloadReference.current.appId };
        sendCoord(message);
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);
}
